import type { LocalizationContext } from "../../lib/api/localization";

export const DEFINITION_CONTEXTS: LocalizationContext[] = [
  "shared",
  "pro",
  "certification",
  "account",
];

export type DraftEntry = {
  id: string;
  key: string;
  value: string;
  /** Original key from server; null if newly added. */
  baselineKey: string | null;
  baselineValue: string;
  deleted: boolean;
};

export type ModuleDraft = {
  loaded: boolean;
  loading: boolean;
  version: number | null;
  entries: DraftEntry[];
  error: string | null;
};

/** languageCode → context → draft */
export type DefinitionsDraftState = Record<
  string,
  Partial<Record<LocalizationContext, ModuleDraft>>
>;

export function emptyModuleDraft(): ModuleDraft {
  return {
    loaded: false,
    loading: false,
    version: null,
    entries: [],
    error: null,
  };
}

export function entriesFromDefinitions(
  definitions: Record<string, string>,
): DraftEntry[] {
  return Object.entries(definitions)
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([key, value]) => ({
      id: key,
      key,
      value,
      baselineKey: key,
      baselineValue: value,
      deleted: false,
    }));
}

export function isEntryDirty(entry: DraftEntry): boolean {
  if (entry.baselineKey == null) {
    // New row: dirty unless deleted (cancelled) or both fields empty and deleted
    return !entry.deleted;
  }
  if (entry.deleted) return true;
  return (
    entry.key !== entry.baselineKey || entry.value !== entry.baselineValue
  );
}

export function entryStatus(entry: DraftEntry): "synced" | "unsaved" {
  return isEntryDirty(entry) ? "unsaved" : "synced";
}

export function buildDefinitionsFromEntries(
  entries: DraftEntry[],
): Record<string, string> {
  const out: Record<string, string> = {};
  for (const entry of entries) {
    if (entry.deleted) continue;
    const key = entry.key.trim();
    if (!key) continue;
    out[key] = entry.value;
  }
  return out;
}

export function findDuplicateKeys(entries: DraftEntry[]): string[] {
  const seen = new Map<string, number>();
  for (const entry of entries) {
    if (entry.deleted) continue;
    const key = entry.key.trim();
    if (!key) continue;
    seen.set(key, (seen.get(key) ?? 0) + 1);
  }
  return [...seen.entries()].filter(([, n]) => n > 1).map(([k]) => k);
}

export function countModuleDirty(draft: ModuleDraft | undefined): number {
  if (!draft?.loaded) return 0;
  return draft.entries.filter(isEntryDirty).length;
}

export function countLanguageDirty(
  langDraft: Partial<Record<LocalizationContext, ModuleDraft>> | undefined,
): number {
  if (!langDraft) return 0;
  return DEFINITION_CONTEXTS.reduce(
    (sum, ctx) => sum + countModuleDirty(langDraft[ctx]),
    0,
  );
}

export function countAllDirty(state: DefinitionsDraftState): number {
  return Object.values(state).reduce(
    (sum, lang) => sum + countLanguageDirty(lang),
    0,
  );
}

export function sectionIdForContext(context: LocalizationContext): string {
  return `module-${context}`;
}

export function newDraftEntry(): DraftEntry {
  return {
    id: `new-${crypto.randomUUID()}`,
    key: "",
    value: "",
    baselineKey: null,
    baselineValue: "",
    deleted: false,
  };
}
